import kebabCase from 'lodash/kebabCase'
import min from 'lodash/min'
import uniq from 'lodash/uniq'
import { useI18n } from 'vue-i18n'
import { isBetween } from '~/assets/date'
import { pathWithoutHash } from '~/assets/util'

const saleStatuses: Record<number, string> = {
  0: 'SALE_STATUS_NONE',
  1: 'SALE_STATUS_FEW_TICKETS',
  2: 'SALE_STATUS_SOLD_OUT',
  3: 'SALE_STATUS_CANCELLED',
  4: 'SALE_STATUS_NOT_SCHEDULED',
}

const saleStatusIcons: Record<number, string> = {
  1: 'app-circled-exclamation-mark',
  2: 'app-lock',
  3: 'app-circled-exclamation-mark',
  4: 'app-lock',
}

const vriendenLoterijCustomerTagIds = [
  499, // 'VriendenLoterij VIP-KAART'
]

// Event tag in Tixly that determines whether we should show the VIP card validation banner.
const vriendenLoterijVipCardValidationEventTagName = 'VL-Vipkaartvalidatie'

// Frozen: a plain object here is a Node-process-wide singleton (shared across every SSR request this
// module's process handles), so this locks in that it's never accidentally reassigned rather than relying
// on convention.
export const educationSchoolTypes = Object.freeze({
  518: 'customer-tag-po-regulier',
  519: 'customer-tag-speciaal',
  520: 'customer-tag-vo-vmbo',
  521: 'customer-tag-vo-have-vwo',
  522: 'customer-tag-mbo',
  1583: 'customer-tag-voorschool',
})

/** The Tix sale fields of an event, as delivered by the CMS and the bridge. */
type TixSale = {
  production?: CmsData | null
  saleDates?: Record<string, string> | null
  saleEndDate?: string | null
  saleStatus?: number | null
}

export function setupTix() {
  const tixStore = useTixStore()
  const favoritesStore = useFavoritesStore()
  const config = useRuntimeConfig()
  const { locale } = useI18n()
  const bridgeFetch = useBridgeFetch()
  const route = useRoute()
  const currentTixUrl = tixStore.tixEnv === 'education' ? config.public.edutixSiteUrl : config.public.tixSiteUrl

  return {
    // Reactive properties from the store
    tixOrder: computed(() => tixStore.order),
    tixOrderSize: computed(() => tixStore.order?.items.length || 0),
    tixOrderUrl: computed(() => tixStore.order?.url),
    tixProfileUrl: computed(() => tixStore.profile),
    tixUser: computed(() => tixStore.user),
    isEducationCustomer: computed(() => tixStore.isEducationCustomer),

    // Update the store
    tixUpdate(data: { order?: CmsData; profile?: CmsData; user?: CmsData }) {
      tixStore.update(data)
      favoritesStore.setUser(data?.user)
      // Use a timeout of 2.5 sec. Tixly tends to create a new customer
      // if we do this call too fast after login - [CGBR-4300]
      setTimeout(async () => {
        if (!tixStore.customerId && data?.user?.id && data?.user?.hash) {
          tixStore.setCustomerId(await bridgeFetch(`/api/tix/customer/${data?.user?.id}?hash=${data?.user?.hash}`, { method: 'GET' }))
          console.info(`Called /api/tix/customer/${data?.user?.id}?hash=${data?.user?.hash}`)
        }
      }, 2500)
    },

    // Static urls are dependent on the tix site url and the current locale
    tixIframeUrl: `${currentTixUrl}/${locale.value}/itix`,
    // The returnUrl is rendered into an SSR'd <a href>, so it has to drop the fragment like any other
    // server-rendered value; the browser keeps the visitor's own hash on the way back regardless.
    tixLoginUrl: `${currentTixUrl}/${locale.value}/login?returnUrl=${encodeURIComponent(`${config.public.wwwSiteUrl}${pathWithoutHash(route.fullPath)}`)}`,
    tixBaseLoginUrl: `${currentTixUrl}/${locale.value}/login`,
    tixOrigin: currentTixUrl,
  }
}

/**
 * Returns a closure that returns reactive helpers related to the price *and* the logged in customer, i.e. minPrice can change when you login
 */
export function setupPrepareTixPricing() {
  const { tixUser } = setupTix()

  return (pricing?: CmsData[] | null, eventCustomerTags?: CmsData[] | null, eventTags?: (CmsData | string)[] | null) => {
    const pricing_ = pricing || []
    const eventCustomerTags_ = eventCustomerTags || []
    const eventTags_ = eventTags || []

    const customerTags = computed(() => [null, ...(unref(tixUser)?.tags.map((tag: CmsData) => tag.id) || [])])
    const availablePrices = computed(() =>
      pricing_.filter(
        (price) =>
          // The current customer must be allowed to see this price
          unref(customerTags).includes(price.customer_tag_id) &&
          // The price must be active
          isBetween('now', price.active_from, price.active_to, true),
      ),
    )
    const minPrice = computed(() => {
      // The minPrice is only based on the prices with the 'null' tag
      const defaultPrices = pricing_.filter((price) => !price.customer_tag_id)
      // logic is a bit odd: use the lowest price of the first "ticket_type" group, not the lowest price overall.
      // this is assuming the sorting is correct from the TixImporter from the Bridge
      const firstGroupName = defaultPrices?.[0]?.ticket_type
      const firstGroupPricing = defaultPrices.filter((entry) => entry.ticket_type === firstGroupName).map((entry) => entry.price)
      return min(firstGroupPricing)
    })
    const hasVriendenloterijTicketType = computed(() => eventCustomerTags_.some((customerTag) => vriendenLoterijCustomerTagIds.includes(customerTag.tixId)))

    const hasVriendenloterijVipCardValidationTag = computed(() =>
      eventTags_.some((tag) => {
        // Support both string tags and objects (e.g. { name: '...' } or { title: '...' })
        if (typeof tag === 'string') return tag === vriendenLoterijVipCardValidationEventTagName
        return tag?.name === vriendenLoterijVipCardValidationEventTagName || tag?.title === vriendenLoterijVipCardValidationEventTagName
      }),
    )

    const showVriendenloterijVipCardBanner = computed(() => unref(hasVriendenloterijTicketType) || unref(hasVriendenloterijVipCardValidationTag))

    const getZoneOrdinal = (zone: string) => {
      if (!zone) return 666
      const lower = zone.toLowerCase()
      const named: Record<string, number> = { goud: 1, gold: 1, zilver: 2, silver: 2 }
      if (named[lower] !== undefined) return named[lower]
      if (/^\d+\+/.test(zone)) return 2 + parseInt(zone, 10) * 2 - 1
      if (/^\d+/.test(zone)) return 2 + parseInt(zone, 10) * 2
      return 666
    }

    const matrix = computed(() => ({
      zones: uniq(pricing_.map((price) => price.price_zone)).sort((a, b) => getZoneOrdinal(a) - getZoneOrdinal(b)),
      types: uniq(unref(availablePrices).map((price) => price.ticket_type)),
      getPrice(zone: string, type: string) {
        for (const price of pricing_) {
          if (price.price_zone === zone && price.ticket_type === type) {
            return price.price
          }
        }
      },
    }))

    return {
      availablePrices,
      customerTags,
      hasVriendenloterijTicketType,
      hasVriendenloterijVipCardValidationTag,
      showVriendenloterijVipCardBanner,
      matrix,
      minPrice,
    }
  }
}

/**
 * Returns reactive helpers related to the price *and* the logged in customer, i.e. minPrice can change when you login
 */
export function setupTixPricing(pricing?: CmsData[] | null, eventCustomerTags?: CmsData[] | null, eventTags?: (CmsData | string)[] | null) {
  return setupPrepareTixPricing()(pricing, eventCustomerTags, eventTags)
}

/**
 * Returns a closure that returns reactive helpers related to the sale *and* the logged in customer, i.e. saleIsAvailable can change when you login
 */
export function setupPrepareTixSale() {
  const route = useRoute()
  const { tixUser } = setupTix()
  return ({ production, saleDates, saleEndDate, saleStatus }: TixSale) => {
    const saleDates_ = saleDates || {}
    const saleEndDate_ = saleEndDate ? new Date(saleEndDate) : new Date('2222-01-01T12:00:00.000Z')
    const customerTags = computed(() => ['_default', ...(unref(tixUser)?.tags.map((tag: CmsData) => String(tag.id)) || [])])
    const saleAvailableFromDate = computed(() => {
      // There will never be a sale date when the event has already passed
      if (saleEndDate && saleEndDate_ < new Date()) {
        return undefined
      }

      const availableDates = Object.entries(saleDates_)
        // Filter out all dates that are not part of the customerTags
        .filter(([customerTag, _dateString]) => unref(customerTags).includes(customerTag))
        // Map to dateString
        .map(([_customerTag, dateString]) => dateString)
        // Sort by dateString to get the earliest date
        .sort()
      // Return the earliest or undefined
      const [earliest] = availableDates
      if (earliest) {
        const earliestDate = new Date(earliest)
        // Check that the date is before the saleEndDate
        if (earliestDate < saleEndDate_) {
          return earliestDate
        }
      }
      // No available sale start date
      return undefined
    })

    const saleIsAvailable = computed(() => {
      // The event is for sale when saleStatus is either 0 or 1 AND when the available sale date is in the past
      const availableFrom = unref(saleAvailableFromDate)
      return [0, 1].includes(saleStatus || 0) && availableFrom !== undefined && availableFrom < new Date()
    })

    const saleIsAvailableInFuture = computed(() => {
      // The event is currently not for sale but will be in the future
      const availableFrom = unref(saleAvailableFromDate)
      return unref(saleIsAvailable) === false && availableFrom !== undefined && availableFrom > new Date()
    })

    const saleStatus_ = computed(() => {
      if (unref(saleIsAvailable)) {
        // The sale is available.
        // We want to show the saleStatus or fallback to None (which is the same as showing the sale button)
        return saleStatus || 0
      } else {
        // The sale is *not* available.
        // When the status is 'last cards', i.e. `1`, then we do NOT want to show this (it makes no sense to say, last available, without having a sale button)
        // When the status is anything else, then use that status (i.e. the event is not available because it is sold out, code `2`)
        return saleStatus === 1 ? 0 : (saleStatus ?? 0)
      }
    })

    const saleIcon = computed(() => {
      return saleStatusIcons[unref(saleStatus_)]
    })

    const saleTemplate = computed(() => {
      const status = unref(saleStatus_)
      return status ? `tix-${kebabCase(saleStatuses[status])}` : undefined
    })

    if (/tix|yes|true|1/.test(String(unref(route).query.debug ?? ''))) {
      // We want to show some debug info to make it easier (also on production!) to determine why a sale button is shown
      const log = (color: string) => {
        const messages = [
          `Event: ${production?.title || 'Unknown event'}`,
          `Available (now, future): ${unref(saleIsAvailable) ? 'yes' : 'no'}, ${unref(saleIsAvailableInFuture) ? 'yes' : 'no'}`,
          `Status: ${saleStatuses[unref(saleStatus_)]}`,
          `Customer tags: ${unref(customerTags)}`,
          `Available between: ${unref(saleAvailableFromDate)?.toISOString()} and ${saleEndDate_?.toISOString()}`,
        ]

        console.debug(`%c${messages.join('\n')}`, `border-left: 2px solid ${color}; color: ${color}; padding-left: 5px`)
      }
      log('#003366')
      watch([customerTags, saleAvailableFromDate, saleIsAvailable, saleIsAvailableInFuture, saleStatus_], () => log('#660033'))
    }

    return {
      saleAvailableFromDate,
      saleIcon,
      saleIsAvailable,
      saleIsAvailableInFuture,
      saleStatus: saleStatus_,
      saleTemplate,
    }
  }
}

/**
 * Returns reactive helpers related to the sale *and* the logged in customer, i.e. saleIsAvailable can change when you login
 */
export function setupTixSale(tixSale: TixSale) {
  return setupPrepareTixSale()(tixSale)
}

/**
 * This is a simplified version of setupPrepareTixSale.
 * The events are bookable via the "saleStatus" and from the "saleDates".
 * Logic based from old Symfony stack in "\Zicht\Bundle\ConcEducationBundle\Entity\Event::isBookable".
 */
export function setupPrepareEducationTixSale() {
  const route = useRoute()
  return ({ saleDates, saleStatus, saleEndDate }: TixSale) => {
    const saleDates_ = saleDates || {}
    const saleEndDate_ = saleEndDate ? new Date(saleEndDate) : new Date('2222-01-01T12:00:00.000Z')
    const educationTags = Object.keys(educationSchoolTypes).map((tagId) => String(tagId)) || []
    const bookableFromDate = computed(() => {
      // There will never be a sale date when the event has already passed
      if (saleEndDate && saleEndDate_ < new Date()) {
        return undefined
      }

      const availableDates = Object.entries(saleDates_)
        // Filter out all dates that are not part of the customerTags
        .filter(([customerTag, _dateString]) => educationTags.includes(customerTag))
        // Map to dateString
        .map(([_customerTag, dateString]) => dateString)
        // Sort by dateString to get the earliest date
        .sort()

      // Return the earliest or undefined
      const [earliest] = availableDates
      if (earliest) {
        const earliestDate = new Date(earliest)
        // Check that the date is before the saleEndDate
        if (earliestDate < saleEndDate_) {
          return earliestDate
        }
      }

      // No available sale start date
      return undefined
    })

    const isBookable = computed(() => {
      // The event is for sale when saleStatus is either 0 or 1 AND when the available sale date is in the past
      const availableFrom = unref(bookableFromDate)
      return [0, 1].includes(saleStatus || 0) && availableFrom !== undefined && availableFrom < new Date()
    })

    if (/tix|yes|true|1/.test(String(unref(route).query.debug ?? ''))) {
      // We want to show some debug info to make it easier to determine if an event is bookable
      const log = (color: string) => {
        const messages = [
          `Bookable: ${unref(isBookable) ? 'yes' : 'no'}`,
          `Status: ${saleStatus == null ? undefined : saleStatuses[saleStatus]}`,
          `Available between: ${unref(bookableFromDate)?.toISOString()} and ${saleEndDate_?.toISOString()}`,
        ]

        console.debug(`%c${messages.join('\n')}`, `border-left: 2px solid ${color}; color: ${color}; padding-left: 5px`)
      }
      log('#003366')
      watch([isBookable, bookableFromDate], () => log('#660033'))
    }

    return {
      isBookable,
      bookableFromDate,
    }
  }
}
